/**
 * SOPPreview — quick preview dialog when clicking a SOP name. Shows every
 * important thing at a glance: meta, run stats, ordered steps, body,
 * attachments, recent runs and version history.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative, fmtDuration } from "@/lib/format";
import { RichTextDisplay } from "@/components/RichTextEditor";
import { kindIcon } from "@/components/sops/SOPStepEditor";
import { BookOpen, History, Check, Play, Paperclip, Users, UserCheck, UserX } from "lucide-react";

export default function SOPPreview({ sop, departments, onClose, onRun }) {
  const { can, isAdminRole } = useWorkspacePerms();
  const isAdmin = isAdminRole || can("sop.manage");
  const [ov, setOv] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  // Feb 2026 · Backend-sourced ack state so a re-opened preview after
  // a version bump correctly shows "Mark as read" again instead of
  // stale "✓ acknowledged" from the previous session.
  const [ackStatus, setAckStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAckRoster, setShowAckRoster] = useState(false);

  useEffect(() => {
    api.get(`/sops/${sop.id}/overview`)
      .then((r) => setOv(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load preview")));
    // Pull ack-status (includes admin roster when applicable).
    api.get(`/sops/${sop.id}/ack-status`)
      .then((r) => setAckStatus(r.data || null))
      .catch(() => {});
  }, [sop.id]);

  const ack = async () => {
    setBusy(true);
    try {
      await api.post(`/sops/${sop.id}/ack`);
      toast.success("Acknowledged");
      const { data } = await api.get(`/sops/${sop.id}/ack-status`);
      setAckStatus(data || null);
    }
    catch (e) { toast.error(apiErrorMessage(e, "Ack failed")); }
    finally { setBusy(false); }
  };
  const loadVersions = async () => {
    if (versions.length === 0) {
      try { const { data } = await api.get(`/sops/${sop.id}/versions`); setVersions(data || []); }
      catch (e) { toast.error(apiErrorMessage(e, "Couldn't load history")); }
    }
    setShowVersions(true);
  };

  const stats = ov?.stats;
  const steps = ov?.steps || [];

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto" data-testid="sop-view">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <BookOpen className="w-4 h-4" />
            <span>{sop.title}</span>
            <span className="chip text-[10px] chip-internal">{sop.version || "v1"}</span>
            <Button size="sm" variant="outline" className="rounded-none ml-auto" onClick={loadVersions} data-testid="sop-view-history"><History className="w-3.5 h-3.5 mr-1" /> History</Button>
            <Button size="sm" className="rounded-none bg-primary hover:bg-primary/90" onClick={onRun} data-testid="sop-view-run"><Play className="w-3.5 h-3.5 mr-1" /> Run this SOP</Button>
            {sop.requires_ack && ackStatus && !ackStatus.my_acked && (
              <Button size="sm" className="rounded-none" onClick={ack} disabled={busy} data-testid="sop-ack-btn"><Check className="w-3.5 h-3.5 mr-1" /> {busy ? "…" : "Mark as read"}</Button>
            )}
            {ackStatus?.my_acked && <span className="chip text-[10px] chip-approved" data-testid="sop-ack-done">✓ acknowledged</span>}
            {isAdmin && sop.requires_ack && ackStatus?.acked_count != null && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-none"
                onClick={() => setShowAckRoster(true)}
                data-testid="sop-ack-roster-btn"
                title="See who has / hasn't acknowledged the current version"
              >
                <Users className="w-3.5 h-3.5 mr-1" />
                {ackStatus.acked_count}/{ackStatus.total} acknowledged
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Meta chips */}
          <div className="flex flex-wrap gap-1">
            {sop.category && <span className="chip text-[10px]">{sop.category}</span>}
            {sop.requires_ack && <span className="chip text-[10px] chip-pending">requires ack</span>}
            {(sop.department_ids || []).map((did) => {
              const d = departments.find((x) => x.id === did);
              return d ? <span key={did} className="chip text-[10px]">{d.name}</span> : null;
            })}
            {(sop.tags || []).map((t) => <span key={t} className="chip text-[10px]">#{t}</span>)}
          </div>

          {/* Run stats */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="sop-preview-stats">
              <MiniStat label="Runs" value={stats.total_runs} />
              <MiniStat label="Completed" value={stats.completed} />
              <MiniStat label="In progress" value={stats.in_progress} />
              <MiniStat label="Completion" value={stats.completion_rate_pct != null ? `${stats.completion_rate_pct}%` : "—"} />
              <MiniStat label="Avg time" value={fmtDuration(stats.avg_duration_seconds)} />
            </div>
          )}

          {/* Steps */}
          <div>
            <div className="label-eyebrow text-[10px] mb-1.5">Steps ({steps.length})</div>
            {steps.length === 0 ? (
              <div className="text-xs text-muted-foreground border border-dashed border-border p-3">No steps configured — this SOP is a document only.</div>
            ) : (
              <div className="border border-border" data-testid="sop-preview-steps">
                {steps.map((s, i) => {
                  const Icon = kindIcon(s.kind);
                  const isHtml = s.description && /<[a-z][\s\S]*>/i.test(s.description);
                  return (
                    <div key={s.id} className="px-3 py-2 border-b border-border last:border-b-0" data-testid={`sop-preview-step-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-muted-foreground w-4">{i + 1}</span>
                        <Icon className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="font-semibold text-xs flex-1">{s.title}</span>
                        <span className="chip text-[9px]">{s.kind}</span>
                        {s.is_required === false && <span className="chip text-[9px] chip-pending">optional</span>}
                      </div>
                      {s.description && (
                        <div className="pl-9 mt-0.5">
                          {isHtml
                            ? <RichTextDisplay html={s.description} className="text-[11px]" />
                            : <div className="text-[11px] text-muted-foreground whitespace-pre-wrap">{s.description}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Body */}
          {sop.body && (
            <div>
              <div className="label-eyebrow text-[10px] mb-1.5">Reference document</div>
              <div className="border border-border p-3 bg-muted/10">
                <RichTextDisplay html={sop.body} testid="sop-view-body" />
              </div>
            </div>
          )}

          {/* Attachments */}
          {(sop.attachments || []).length > 0 && (
            <div>
              <div className="label-eyebrow text-[10px] mb-1.5 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments</div>
              <div className="flex flex-wrap gap-1">
                {sop.attachments.map((a) => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="chip text-[10px] hover:bg-muted" data-testid={`sop-att-${a.id}`}>
                    {a.title || a.kind}: {a.url.slice(0, 40)}…
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Recent runs */}
          {(ov?.recent_runs || []).length > 0 && (
            <div>
              <div className="label-eyebrow text-[10px] mb-1.5">Recent runs</div>
              <div className="border border-border" data-testid="sop-preview-runs">
                {ov.recent_runs.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-border last:border-b-0 text-xs">
                    <span className={`chip text-[9px] ${r.status === "completed" ? "chip-approved" : r.status === "cancelled" ? "chip-rejected" : "chip-pending"}`}>{r.status}</span>
                    <span className="truncate flex-1">{r.assignee?.name || r.assignee?.email || r.assignee_id?.slice(0, 8)}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{r.duration_seconds != null ? fmtDuration(r.duration_seconds) : ""}</span>
                    <span className="text-[10px] text-meta">{fmtRelative(r.started_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showVersions && (
          <div className="mt-4 border-t border-border pt-4" data-testid="sop-version-list">
            <div className="label-eyebrow mb-2 flex items-center gap-2"><History className="w-3 h-3" /> Version history</div>
            {versions.length === 0 ? <div className="text-xs text-muted-foreground">No prior versions saved yet.</div>
              : <div className="space-y-2">
                {versions.map((v) => (
                  <details key={v.id} className="border border-border" data-testid={`sop-version-${v.id}`}>
                    <summary className="px-3 py-2 cursor-pointer text-sm flex items-center gap-2 hover:bg-muted/30">
                      <span className="chip text-[10px] chip-internal">{v.version || "—"}</span>
                      <span className="text-xs text-muted-foreground">saved {fmtRelative(v.saved_at)}</span>
                      {v.change_summary && <span className="text-xs italic truncate">— {v.change_summary}</span>}
                    </summary>
                    <div className="px-3 py-3 border-t border-border bg-muted/20">
                      <RichTextDisplay html={v.body} testid={`sop-version-body-${v.id}`} />
                    </div>
                  </details>
                ))}
              </div>}
          </div>
        )}
      </DialogContent>
      {showAckRoster && (
        <SOPAckRosterDialog
          status={ackStatus}
          sopTitle={sop.title}
          onClose={() => setShowAckRoster(false)}
        />
      )}
    </Dialog>
  );
}

function SOPAckRosterDialog({ status, sopTitle, onClose }) {
  if (!status) return null;
  const acked = status.acked || [];
  const pending = status.pending || [];
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="sop-ack-roster-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Users className="w-4 h-4" />
            <span className="truncate">{sopTitle} · ack roster</span>
            <span className="chip text-[10px] chip-internal">{status.version || "v?"}</span>
            <span className="ml-auto text-[11px] text-muted-foreground font-normal">
              {status.acked_count}/{status.total} acknowledged
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <section data-testid="sop-ack-pending-section">
            <div className="label-eyebrow text-[10px] mb-1.5 flex items-center gap-1 text-amber-700">
              <UserX className="w-3 h-3" /> Pending · {pending.length}
            </div>
            {pending.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-2">
                Everyone has acknowledged this version.
              </div>
            ) : (
              <ul className="border border-border max-h-80 overflow-y-auto divide-y divide-border">
                {pending.map((p) => (
                  <li key={p.user_id} className="px-2 py-1.5 text-[12px]"
                      data-testid={`sop-ack-pending-${p.user_id}`}>
                    <div className="truncate font-medium">{p.name}</div>
                    {p.email && <div className="truncate text-[10px] text-muted-foreground">{p.email}</div>}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section data-testid="sop-ack-done-section">
            <div className="label-eyebrow text-[10px] mb-1.5 flex items-center gap-1 text-emerald-700">
              <UserCheck className="w-3 h-3" /> Acknowledged · {acked.length}
            </div>
            {acked.length === 0 ? (
              <div className="text-[11px] text-muted-foreground italic border border-dashed border-border p-2">
                Nobody has acknowledged this version yet.
              </div>
            ) : (
              <ul className="border border-border max-h-80 overflow-y-auto divide-y divide-border">
                {acked.map((p) => (
                  <li key={p.user_id} className="px-2 py-1.5 text-[12px]"
                      data-testid={`sop-ack-done-${p.user_id}`}>
                    <div className="truncate font-medium">{p.name}</div>
                    <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                      {p.email && <span className="truncate">{p.email}</span>}
                      {p.acked_at && <span className="shrink-0" title={new Date(p.acked_at).toLocaleString()}>{fmtRelative(p.acked_at)}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-border p-2">
      <div className="label-eyebrow text-[9px]">{label}</div>
      <div className="text-base font-black font-mono">{value ?? "—"}</div>
    </div>
  );
}
